import {
  type BoardCardAddress,
  type BoardCardRoom,
  type CellAddress,
  type ItemId,
  useBoardCell,
  useBoardCellHasCardMoves,
  useBoardElementRef,
  useBoardItemLabel,
  useBoardLabels,
} from '@github-ui/board-core'
import {VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type ReactNode, type RefObject, use, useLayoutEffect, useMemo} from 'react'

import headingStyles from '../BoardHeadings.module.css'
import {BoardWindowSpacer} from '../window/BoardWindow'
import {useBoardCardWindow} from '../window/use-board-card-window'
import {useAnnounceOnce} from './announce-once'
import {BoardCardFrame} from './BoardCardFrame'
import {hasContent} from './BoardCardMenu'
import type {BoardCardMenuPlacement} from './BoardCardMenuButton'
import styles from './BoardCellFrame.module.css'
import {BoardItemPaging} from './BoardPaging'
import {HeldCardSizeContext, useHeldCardSize} from './held-card-size'

export type BoardCellFrameProps<TItem> = {
  address: CellAddress
  /**
   * The cards loaded in this cell, in order. They come from the caller because reading
   * them is the one thing that needs the item type, and keeping it here is what lets
   * `useBoardCell` stay free of the generic.
   *
   * Must be the same array `useBoardData` holds for this cell, so it lines up with the
   * ids the hook returns.
   */
  items: readonly TItem[]
  renderCard: (item: TItem) => ReactNode
  /** The caller's own items for one card's menu, above the move controls. See `BoardBody`. */
  renderCardActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  /** The caller's own items after the move controls. See `BoardBody`. */
  renderCardTrailingActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  /** See `BoardBody`. */
  onCardMenuIntent?: (item: TItem) => void
  /**
   * See `BoardBody`.
   * @default 'corner'
   */
  cardMenuPlacement?: BoardCardMenuPlacement
  /**
   * What an empty cell shows. When it is not supplied, the `emptyCell` label remains
   * available to assistive technology without drawing visible copy. A renderer that
   * returns `null` suppresses the empty state for a particular cell.
   */
  renderEmptyCell?: (address: CellAddress) => ReactNode
  className?: string
}

/**
 * One cell: the intersection of a column and a row.
 *
 * A pair the caller never supplied and a pair supplied with no items reach this
 * component the same way, so they render identically. That matters because the caller's
 * `cells` is sparse (it holds only the pairs something is in), and a board that drew
 * absent pairs differently would lose a cell every time a column and a row happened not
 * to overlap.
 */
export function BoardCellFrame<TItem>({
  address,
  items,
  renderCard,
  renderCardActions,
  renderCardTrailingActions,
  onCardMenuIntent,
  cardMenuPlacement = 'corner',
  renderEmptyCell,
  className,
}: BoardCellFrameProps<TItem>) {
  const {
    itemIds,
    cellProps,
    bodyProps,
    bodyRef,
    focusedItemIndex,
    isCardFocusPending,
    isEmpty,
    isLoadingItems,
    hasMoreItems,
    heading,
    headingLevel,
    isOver,
    canReorderCards,
    dropIndex,
    cardRoom,
  } = useBoardCell(address)
  const labels = useBoardLabels()
  const getItemLabel = useBoardItemLabel<TItem>()
  const hasCardMoves = useBoardCellHasCardMoves(address)
  const Heading = `h${headingLevel}` as const
  const heldIndex = cardRoom?.heldIndex ?? null
  const roomIndex = cardRoom?.index ?? null
  const heldCardSize = useHeldCardSize(heldIndex === null && roomIndex !== null)

  // A page arriving is a status message: the board pages by being scrolled rather than by
  // anything the reader pressed, so nobody asked for it.
  useAnnounceOnce(labels.loadingItems, isLoadingItems)

  // Only the cards on screen are drawn, plus whichever one the board's focus position
  // names and the one being moved, so a cell holding five hundred costs what fits rather
  // than what it holds.
  const {slots, listRef, measureRef} = useBoardCardWindow({
    bodyRef,
    itemIds,
    focusedIndex: focusedItemIndex,
    isFocusPending: isCardFocusPending,
    heldIndex,
    roomIndex,
    heldCardSize,
  })

  useHeldCardBlockSize(bodyRef, heldIndex)

  const drawnIndexes = useMemo(
    () => new Set(slots.flatMap(slot => (slot.type === 'card' ? [slot.index] : []))),
    [slots],
  )

  /*
    Which drawn card the insertion marker goes on, and which of its edges.

    `dropIndex` is a place among the cards the cell has loaded, and only the drawn ones have
    an edge to mark. The index itself is the usual answer, marked before the card it names.
    One past the last card the cell drew is the other, marked after that card, because no
    card carries that index: there is either no such card or it is one the window left out.
  */
  const insertionAt = useMemo(() => placeAmongDrawn(dropIndex, drawnIndexes), [dropIndex, drawnIndexes])
  const roomMode = cardRoom === null ? undefined : cardRoomMode(cardRoom)
  // A cell that opens room for the held card already shows where it lands, so only a cell
  // ordered by a sort is ringed. Read from the caller's ranking rather than from the room,
  // which a ranked cell measures a frame after the pointer arrives.
  const opensRoom = canReorderCards || heldIndex !== null
  const roomAt = useMemo(
    () => (drawnIndexes.size === 0 && cardRoom?.index === 0 ? START : placeAmongDrawn(cardRoom?.index, drawnIndexes)),
    [cardRoom, drawnIndexes],
  )

  // The default copy remains available to assistive technology without filling every
  // empty cell with repeated visible text. A renderer that returns `null` has decided
  // this cell says nothing, and falling back there would overrule it, the same reading
  // of a render prop's `null` that `BoardRowFrame` takes.
  const emptyState =
    renderEmptyCell === undefined ? <VisuallyHidden>{labels.emptyCell}</VisuallyHidden> : renderEmptyCell(address)

  return (
    <div
      {...cellProps}
      data-drop-target={isOver ? '' : undefined}
      className={clsx(styles.CellFrame, isOver && !opensRoom && styles.DropTargetRing, className)}
    >
      <div {...bodyProps} data-loading-items={isLoadingItems ? '' : undefined} className={styles.Body}>
        {heading === undefined ? null : <Heading className={headingStyles.HiddenHeading}>{heading}</Heading>}
        {/*
          The cards, drawn whether or not the cell holds any. An empty cell still gives
          keyboard reordering a stable destination in exactly the cells a person most
          needs to reach.

          Its window draws a run of cards and reserves the height of the rest, so what the
          scrollbar describes is every card the cell has loaded. This is a plain container
          rather than a list: each card is a `<section>` named by its title, and a list
          could only ever count the cards the window drew.
        */}
        <div
          ref={listRef}
          // How the cards move for the room this cell keeps, read by the stylesheet. Present
          // in every cell while a card is being moved, so a room can animate closed as well as
          // open, and gone the moment the move lands so the cards settle without a second move.
          data-card-room={roomMode}
          data-empty={isEmpty ? '' : undefined}
          className={styles.Cards}
        >
          {roomAt === START ? <BoardCardRoom key="room:0" /> : null}
          {slots.flatMap(slot => {
            if (slot.type === 'space') {
              return <BoardWindowSpacer key={`reserved:${slot.before}`} axis="block" size={slot.size} />
            }

            const id = itemIds[slot.index]
            const item = items[slot.index]
            if (id === undefined || item === undefined) return []

            const card = (
              <BoardWindowedCard
                // Every kind of key is namespaced, because an item id is opaque and may be any
                // string at all, including one that reads exactly like a reservation's.
                key={`card:${id}`}
                itemId={id}
                address={address}
                item={item}
                renderCard={renderCard}
                renderCardActions={renderCardActions}
                renderCardTrailingActions={renderCardTrailingActions}
                onCardMenuIntent={onCardMenuIntent}
                cardMenuPlacement={cardMenuPlacement}
                // Built here, where the labels are already read, so a card is handed a string
                // and does not wake for every new accessor the caller passes.
                label={getItemLabel(item)}
                menuLabel={labels.cardActions(getItemLabel(item))}
                hasCardMoves={hasCardMoves}
                index={slot.index}
                insertion={insertionAt?.index === slot.index ? insertionAt.side : undefined}
                isHeld={slot.index === heldIndex}
                measureRef={measureRef}
              />
            )
            if (roomAt === null || roomAt === START || roomAt.index !== slot.index) return card

            // Keyed on where it is, so moving the room mounts a new one rather than moving a
            // card element, which would drop that card's transition.
            const room = <BoardCardRoom key={`room:${cardRoom?.index}`} />
            return roomAt.side === 'before' ? [room, card] : [card, room]
          })}
        </div>
        {/*
          "No cards" is a statement about the whole cell, so it is wrong in a cell that
          has a page waiting: such a cell is not empty, it is unread. `hasMoreItems`
          rather than a count, because the count is a fact the caller stated and may well
          be larger than the nothing that is loaded.
        */}
        {isEmpty && !isLoadingItems && !hasMoreItems ? emptyState : null}
        {/*
          Both halves of "there is more in this cell": the ghost that says so, and the
          loading row that replaces it while a page is in flight. It renders after the
          cards, at the end of what is loaded, because that is the place reaching it is
          supposed to mean, and it holds its room open either way so the swap moves
          nobody.
        */}
        <BoardItemPaging address={address} />
      </div>
      {/*
        The cell's fades, drawn over the cards at whichever block edge still has some past
        it. It sits outside the scrolling body because it has to hold still while that
        scrolls, and after it because the stylesheet reads the body's scroll-edge state
        from here.
      */}
      <div className={styles.ScrollFades} />
    </div>
  )
}

type BoardWindowedCardProps<TItem> = {
  itemId: ItemId
  address: CellAddress
  item: TItem
  renderCard: (item: TItem) => ReactNode
  renderCardActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  renderCardTrailingActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  onCardMenuIntent?: (item: TItem) => void
  cardMenuPlacement: BoardCardMenuPlacement
  label: string
  menuLabel: string
  hasCardMoves: boolean
  index: number
  insertion?: 'before' | 'after'
  isHeld: boolean
  measureRef: (node: Element | null) => (() => void) | undefined
}

function BoardWindowedCardImpl<TItem>({
  itemId,
  address,
  item,
  renderCard,
  renderCardActions,
  renderCardTrailingActions,
  onCardMenuIntent,
  cardMenuPlacement,
  label,
  menuLabel,
  hasCardMoves,
  index,
  insertion,
  isHeld,
  measureRef,
}: BoardWindowedCardProps<TItem>) {
  const card = {columnKey: address.columnKey, rowKey: address.rowKey, itemId}
  const actions = renderCardActions?.(item, card)
  const trailingActions = renderCardTrailingActions?.(item, card)
  // A card has a menu when it has somewhere to move, because the menu is how a card moves
  // without a pointer, or when the caller gave it actions.
  const hasMenu = hasCardMoves || hasContent(actions) || hasContent(trailingActions)

  return (
    <BoardCardFrame
      itemId={itemId}
      address={address}
      label={label}
      place={{index, measureRef}}
      insertion={insertion}
      className={isHeld ? styles.HeldCard : undefined}
      menu={
        hasMenu
          ? {
              label: menuLabel,
              actions,
              trailingActions,
              onIntent: onCardMenuIntent ? () => onCardMenuIntent(item) : undefined,
              placement: cardMenuPlacement,
            }
          : undefined
      }
    >
      {renderCard(item)}
    </BoardCardFrame>
  )
}

/**
 * One drawn card, and the boundary that stops an arrow key redrawing the rest of them.
 *
 * A cell re-renders when the board's focus position moves into or out of it, because the
 * card that position names is one the window has to draw wherever it is. Without a boundary
 * here, that would run the caller's `renderCard` again for every card in the window on
 * every keypress -- and a reader holding an arrow key down is the one moment the board can
 * least afford it.
 *
 * The props are the scalars the cell already has rather than the `place` object, because an
 * object built in the loop would be a new one every render and there would be nothing to
 * compare.
 *
 * `memo` erases the type parameter, so the original signature is put back. It is a
 * restatement of the function's own type rather than a widening of it, which is the
 * standard way to memoise a generic component.
 */
const BoardWindowedCard = memo(BoardWindowedCardImpl) as typeof BoardWindowedCardImpl

/**
 * The placeholder in the room a cell keeps for the card being moved.
 *
 * Hidden from assistive technology, like `BoardWindowSpacer`: it carries nothing, and the
 * card it stands for is still in the document. It must never be given content or a
 * focusable descendant.
 */
function BoardCardRoom() {
  return <div aria-hidden="true" className={styles.CardRoom} />
}

/** The room sits in a list that has drawn no card, so there is no card to place it beside. */
const START = 'start'

type DrawnPlace = {index: number; side: 'before' | 'after'}

/**
 * Which drawn card a place among the loaded cards sits beside: in front of the card it
 * names, or after the last drawn card when it is one past the end of what is drawn.
 */
function placeAmongDrawn(index: number | null | undefined, drawn: ReadonlySet<number>): DrawnPlace | null {
  if (index === null || index === undefined) return null
  if (drawn.has(index)) return {index, side: 'before'}
  if (drawn.has(index - 1)) return {index: index - 1, side: 'after'}

  return null
}

type CardRoomMode = 'rest' | 'open' | 'push' | 'pull' | 'stay' | 'close'

/**
 * Which cards move for a room, in the words the stylesheet reads.
 *
 * - `open`: another cell's card goes here, so every card from the room on moves down.
 * - `push` and `pull`: the held card goes above or below its own slot in this cell, so the
 *   cards between the two move down or up.
 * - `stay`: the held card goes back where it is, so the room is its own slot.
 * - `close`: the held card goes to another cell, so the cards after it close up.
 * - `rest`: a card is moving elsewhere and nothing here moves.
 */
function cardRoomMode({index, heldIndex}: BoardCardRoom): CardRoomMode {
  if (heldIndex === null) return index === null ? 'rest' : 'open'
  if (index === null) return 'close'
  if (index === heldIndex) return 'stay'

  return index < heldIndex ? 'push' : 'pull'
}

/**
 * Writes the held card's height to the board as `--board-held-card-block-size`, which sizes
 * the room every cell keeps for it, and to the cells' windows. Read from the cell the card came
 * from, as the drag starts, and left in place afterwards: nothing reads it at rest and the next
 * drag writes its own.
 */
function useHeldCardBlockSize(bodyRef: RefObject<HTMLElement | null>, heldIndex: number | null): void {
  const boardElementRef = useBoardElementRef()
  const heldCardSize = use(HeldCardSizeContext)

  useLayoutEffect(() => {
    const board = boardElementRef.current
    const held = heldIndex === null ? null : bodyRef.current?.querySelector(`[data-index="${heldIndex}"]`)
    if (!board || !held) return

    const {height} = held.getBoundingClientRect()
    board.style.setProperty('--board-held-card-block-size', `${height}px`)
    heldCardSize?.set(height)
  }, [boardElementRef, bodyRef, heldCardSize, heldIndex])
}
